/**
 * FraudNet is PayPal's browser-data collector: it fingerprints the checkout page and PayPal Risk
 * Services scores that data when we create the order. Two things have to line up for it to work —
 * the `f` id embedded in the page's FraudNet config block and the `PAYPAL-CLIENT-METADATA-ID`
 * header on the Create Order call.
 *
 * FormSection loads this once on mount for every merchant boarded with PayPal, so collection starts
 * as early in the page's life as possible and covers boarded merchants whether or not they end up
 * showing a PayPal button. The id is cached at module level: consumers read it back with
 * getFraudnetSessionId() at the moment they need it rather than holding their own copy, so a
 * component that mounts before FormSection (Vue mounts children first) or remounts mid-checkout
 * still reports the id the page actually collected under.
 */

// Required by FraudNet: the loader looks for a script of this exact fncls to read its parameters.
const FNCLS = 'fnparams-dede7cc5-15fd-4c75-a9f4-36c430ee3a99';
const FRAUDNET_URL = 'https://c.paypal.com/da/r/fb.js';
const PAGE_ID = 'checkout-page';
// Both `f` and `s` are capped at 32 characters by PayPal.
const MAX_PARAM_LENGTH = 32;

let sessionId = null;

// 16 random bytes hex-encoded, which is exactly the 32 characters PayPal allows for `f`.
function generateSessionId() {
  const bytes = new Uint8Array(16);
  if (window.crypto && window.crypto.getRandomValues) {
    window.crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  return Array.prototype.map.call(bytes, byte => `0${byte.toString(16)}`.slice(-2)).join('');
}

// PayPal's suggested shape for the source website identifier is `<merchant_id>_<page_id>`.
function buildSourceId(merchantId) {
  const prefix = merchantId ? `${merchantId}_` : '';
  return `${prefix}${PAGE_ID}`.slice(0, MAX_PARAM_LENGTH);
}

/**
 * The session id the page collected under, for the `PAYPAL-CLIENT-METADATA-ID` header. Empty until
 * loadFraudnet has successfully injected the scripts — callers send no header in that case.
 *
 * @returns {string} the 32-character FraudNet session id, or '' if FraudNet never loaded.
 */
export function getFraudnetSessionId() {
  return sessionId || '';
}

/**
 * Inject the FraudNet config block and loader on first call and return the session id. Later calls
 * inject nothing and return the same id.
 *
 * @param {string} merchantId PayPal merchant id, used to build the source website identifier.
 * @returns {string} the 32-character FraudNet session id.
 */
export default function loadFraudnet(merchantId) {
  if (sessionId) {
    return sessionId;
  }
  const id = generateSessionId();

  const config = document.createElement('script');
  config.type = 'application/json';
  config.setAttribute('fncls', FNCLS);
  config.text = JSON.stringify({
    f: id,
    s: buildSourceId(merchantId),
  });
  document.body.appendChild(config);

  // Appended after the config so FraudNet finds its parameters as soon as it runs.
  const loader = document.createElement('script');
  loader.type = 'text/javascript';
  loader.src = FRAUDNET_URL;
  loader.async = true;
  document.body.appendChild(loader);

  // Cached only once both scripts are in the page. If an append throws, the id is discarded so we
  // never send a metadata id for data FraudNet was never asked to collect.
  sessionId = id;
  return sessionId;
}
